import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import { bracketTyped, browser, isEmptyDocument } from '@atlaskit/editor-common/utils';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';
export var pluginKey = new PluginKey('placeholderPlugin');
function getPlaceholderState(editorState) {
  return pluginKey.getState(editorState);
}
export var placeholderTestId = 'placeholder-test-id';
export function createPlaceholderDecoration(editorState, placeholderText) {
  var pos = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 1;
  var placeholderDecoration = document.createElement('span');
  var placeholderNodeWithText = placeholderDecoration;
  placeholderDecoration.setAttribute('data-testid', placeholderTestId);
  placeholderDecoration.className = 'placeholder-decoration';

  // PM sets contenteditable to false on Decorations so Firefox doesn't display the flashing cursor
  // So adding an extra span which will contain the placeholder text
  if (browser.gecko) {
    var placeholderNode = document.createElement('span');
    placeholderNode.setAttribute('contenteditable', 'true'); // explicitly overriding the default Decoration behaviour
    placeholderDecoration.appendChild(placeholderNode);
    placeholderNodeWithText = placeholderNode;
  }
  placeholderNodeWithText.textContent = placeholderText || ' ';

  // ME-2289 Tapping on backspace in empty editor hides and displays the keyboard
  // Add a editable buff node as the cursor moving forward is inevitable
  // when backspace in GBoard composition
  if (browser.android && browser.chrome) {
    var buffNode = document.createElement('span');
    buffNode.setAttribute('class', 'placeholder-android');
    buffNode.setAttribute('contenteditable', 'true');
    buffNode.textContent = ' ';
    placeholderDecoration.appendChild(buffNode);
  }
  return DecorationSet.create(editorState.doc, [Decoration.widget(pos, placeholderDecoration, {
    side: 0,
    key: 'placeholder'
  })]);
}
function setPlaceHolderState(placeholderText, pos) {
  return {
    hasPlaceholder: true,
    placeholderText: placeholderText,
    pos: pos ? pos : 1
  };
}
var emptyPlaceholder = {
  hasPlaceholder: false
};

// Ignored via go/ees005
// eslint-disable-next-line @typescript-eslint/max-params
function createPlaceHolderStateFrom(isEditorFocused, editorState, isTypeAheadOpen, defaultPlaceholderText, bracketPlaceholderText) {
  if (isTypeAheadOpen !== null && isTypeAheadOpen !== void 0 && isTypeAheadOpen(editorState)) {
    return emptyPlaceholder;
  }
  if (defaultPlaceholderText && isEmptyDocument(editorState.doc)) {
    return setPlaceHolderState(defaultPlaceholderText);
  }
  if (bracketPlaceholderText && bracketTyped(editorState) && isEditorFocused) {
    var $from = editorState.selection.$from;
    // Space is to account for positioning of the bracket
    var bracketHint = '  ' + bracketPlaceholderText;
    return setPlaceHolderState(bracketHint, $from.pos - 1);
  }
  return emptyPlaceholder;
}
export function createPlugin(defaultPlaceholderText, bracketPlaceholderText, api) {
  if (!defaultPlaceholderText && !bracketPlaceholderText) {
    return;
  }
  return new SafePlugin({
    key: pluginKey,
    state: {
      init: function init(_, state) {
        var _api$focus, _api$typeAhead;
        return createPlaceHolderStateFrom(Boolean(api === null || api === void 0 || (_api$focus = api.focus) === null || _api$focus === void 0 || (_api$focus = _api$focus.sharedState.currentState()) === null || _api$focus === void 0 ? void 0 : _api$focus.hasFocus), state, api === null || api === void 0 || (_api$typeAhead = api.typeAhead) === null || _api$typeAhead === void 0 ? void 0 : _api$typeAhead.actions.isOpen, defaultPlaceholderText, bracketPlaceholderText);
      },
      // Ignored via go/ees005
      // eslint-disable-next-line @typescript-eslint/max-params
      apply: function apply(tr, _oldPluginState, _oldEditorState, newEditorState) {
        var _api$focus2, _api$typeAhead3;
        var meta = tr.getMeta(pluginKey);
        var isEditorFocused = Boolean(api === null || api === void 0 || (_api$focus2 = api.focus) === null || _api$focus2 === void 0 || (_api$focus2 = _api$focus2.sharedState.currentState()) === null || _api$focus2 === void 0 ? void 0 : _api$focus2.hasFocus);
        if (meta) {
          if (meta.removePlaceholder) {
            return emptyPlaceholder;
          }
          if (meta.applyPlaceholderIfEmpty) {
            var _api$typeAhead2;
            return createPlaceHolderStateFrom(isEditorFocused, newEditorState, api === null || api === void 0 || (_api$typeAhead2 = api.typeAhead) === null || _api$typeAhead2 === void 0 ? void 0 : _api$typeAhead2.actions.isOpen, defaultPlaceholderText, bracketPlaceholderText);
          }
        }
        return createPlaceHolderStateFrom(isEditorFocused, newEditorState, api === null || api === void 0 || (_api$typeAhead3 = api.typeAhead) === null || _api$typeAhead3 === void 0 ? void 0 : _api$typeAhead3.actions.isOpen, defaultPlaceholderText, bracketPlaceholderText);
      }
    },
    props: {
      decorations: function decorations(editorState) {
        var _api$composition;
        var _getPlaceholderState = getPlaceholderState(editorState),
          hasPlaceholder = _getPlaceholderState.hasPlaceholder,
          placeholderText = _getPlaceholderState.placeholderText,
          pos = _getPlaceholderState.pos;
        var compositionPluginState = api === null || api === void 0 || (_api$composition = api.composition) === null || _api$composition === void 0 ? void 0 : _api$composition.sharedState.currentState();
        if (hasPlaceholder && placeholderText && pos !== undefined && !(compositionPluginState !== null && compositionPluginState !== void 0 && compositionPluginState.isComposing)) {
          return createPlaceholderDecoration(editorState, placeholderText, pos);
        }
        return;
      }
    }
  });
}
export var placeholderPlugin = function placeholderPlugin(_ref) {
  var options = _ref.config,
    api = _ref.api;
  return {
    name: 'placeholder',
    pmPlugins: function pmPlugins() {
      return [{
        name: 'placeholder',
        plugin: function plugin() {
          return createPlugin(options && options.placeholder, options && options.placeholderBracketHint, api);
        }
      }];
    }
  };
};